import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";

export type BuildItem = {
  id: string;
  filename: string;
  version: string | null;
  notes: string | null;
  sizeBytes: number;
  uploadedByName: string;
  createdAt: number;
};

export type BuildGenStatus = "building" | "ready" | "error";
export interface BuildStatus {
  status: BuildGenStatus;
  buildId: string | null;
  filename: string | null;
  commitSha: string | null;
  error: string | null;
}

/** Daemon-proxied download URL for a build (same origin as the API; no cross-domain cookie). */
export function buildDownloadUrl(project: string, id: string): string {
  return `${DAEMON_URL}/builds/download?project=${encodeURIComponent(project)}&id=${encodeURIComponent(id)}`;
}

export async function fetchBranches(project: string): Promise<string[]> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/branches`, { credentials: "include" });
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  const j = (await res.json()) as { branches?: string[] };
  return j.branches ?? [];
}

export async function listBuilds(project: string): Promise<BuildItem[]> {
  const res = await fetch(`${DAEMON_URL}/builds?project=${encodeURIComponent(project)}`, { credentials: "include" });
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  const j = (await res.json()) as { builds?: BuildItem[] };
  return j.builds ?? [];
}

export async function generateBuild(project: string, branch: string): Promise<string> {
  const res = await fetch(`${DAEMON_URL}/builds/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ project, branch: branch.trim() }),
  });
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden");
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try { const j = (await res.json()) as { error?: string; message?: string }; msg = j.message || j.error || msg; } catch { /* */ }
    throw new HttpError(res.status, msg);
  }
  const j = (await res.json()) as { jobId: string };
  return j.jobId;
}

export async function getBuildStatus(jobId: string): Promise<BuildStatus> {
  const res = await fetch(`${DAEMON_URL}/builds/status?jobId=${encodeURIComponent(jobId)}`, { credentials: "include" });
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return (await res.json()) as BuildStatus;
}
